import { useMutation, useQuery } from "@tanstack/react-query";
import { useUtilities, axiosInstance } from "helpers";
import { useEffect } from "react";
import {
  addTemplateContentRequest,
  getAllTemplateContentRequests,
  updateTemplateContentRequest,
  deleteTemplateContentRequest,
  getAllDefaultContentRequest,
  addDefaultContentRequest,
  deleteDefaultContentRequest,
} from "redux/reducers/templateLibrary/contentRequests";
import { invalidateTemplatePhases } from "./phases";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export async function getContentRequests() {
  return axiosInstance
    .post("defaulttaskshow", {
      api_key: localStorage.getItem("apikey"),
      parent_type: "content",
      contentitems: true,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useGetContentRequests() {
  return useQuery(["contentRequests"], getContentRequests);
}

export function useContentRequests() {
  const {
    dispatch,
    contentRequests: { contentRequests, loaded },
  } = useUtilities();

  const { data, isLoading, error } = useGetContentRequests();

  useEffect(() => {
    if (isLoading || loaded) return;
    if (data) dispatch(getAllTemplateContentRequests(data.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data, loaded]);

  return { data: contentRequests, isLoading, error };
}

export async function addContentRequest(request) {
  return axiosInstance
    .post("addlibrarytemplate", {
      api_key: localStorage.getItem("apikey"),
      parent_type: "content",
      module_hint: "content",
      wpsl_tem: localStorage.getItem("apikey"),
      system_status: "0",
      ...request,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useAddContentRequest(redirect = true) {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(addContentRequest, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(addTemplateContentRequest(data.data));
        notification({
          type: "success",
          message: t("libraryTemplate.contentRequests.api.success.added"),
        });
        if (redirect)
          navigate(
            `/${ModulePrefixes.TEMPLATE_LIBRARY}/content-request-templates/${data.data.id}`
          );
        dispatch(invalidateTemplatePhases());
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: err.message,
      });
    },
  });
}

export async function updateContentRequest(request) {
  return axiosInstance
    .post("defaulttaskedit", {
      api_key: localStorage.getItem("apikey"),
      ...request,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useUpdateContentRequest() {
  const { dispatch, notification } = useUtilities();
  return useMutation(updateContentRequest, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(updateTemplateContentRequest(data.data));
        notification({
          type: "success",
          message: t("libraryTemplate.contentRequests.api.success.updated"),
        });
        dispatch(invalidateTemplatePhases());
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: err.message,
      });
    },
  });
}

export async function deleteContentRequest(id) {
  return axiosInstance
    .post("defaulttaskdelete", {
      api_key: localStorage.getItem("apikey"),
      id,
    })
    .then((res) => ({ ...res.data, id }))
    .catch((err) => new Error(err));
}

export function useDeleteContentRequest(redirect = false) {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(deleteContentRequest, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(deleteTemplateContentRequest(data.id));
        notification({
          type: "success",
          message: t("libraryTemplate.contentRequests.api.success.deleted"),
        });
        if (redirect) navigate(`/${ModulePrefixes.TEMPLATE_LIBRARY}/content-request-templates`);
        dispatch(invalidateTemplatePhases());
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: err.message,
      });
    },
  });
}

export async function getDefaultContentRequest() {
  return axiosInstance
    .post("getwpsltasktemp", {
      api_key: localStorage.getItem("apikey"),
      parent_type: "content",
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useGetDefaultContentRequest() {
  return useQuery(["defaultContentRequest"], getDefaultContentRequest);
}

export function useDefaultContentRequest() {
  const {
    dispatch,
    contentRequests: {
      defaultSite: { contentRequests, loaded },
    },
  } = useUtilities();
  const { data, isLoading, error, isRefetching } = useGetDefaultContentRequest();

  useEffect(() => {
    if (isLoading || loaded || isRefetching) return;
    if (data) {
      dispatch(getAllDefaultContentRequest(data.data));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, loaded, data, isRefetching]);

  return { data: contentRequests, isLoading: isLoading || !loaded, error };
}

export async function addDefaultContent(id) {
  return axiosInstance
    .post("adddefaulttemplate", {
      api_key: localStorage.getItem("apikey"),
      id,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useAddDefaultContentRequest() {
  const { notification, dispatch } = useUtilities();
  return useMutation(addDefaultContent, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(addDefaultContentRequest(data.data));
        notification({
          type: "success",
          message: t("libraryTemplate.contentRequests.api.success.addedToSiteTemplate"),
        });
      } else {
        notification({
          message: data.message,
          type: "error",
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: err.message,
      });
    },
  });
}

export async function deleteDefaultContent(id) {
  return axiosInstance
    .post("defaulttaskremove", {
      api_key: localStorage.getItem("apikey"),
      id,
    })
    .then((res) => ({ ...res.data, id }))
    .catch((err) => new Error(err));
}

export function useDeleteDefaultContentRequest() {
  const { notification, dispatch } = useUtilities();
  return useMutation(deleteDefaultContent, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(deleteDefaultContentRequest(data.id));
        notification({
          type: "success",
          message: t("libraryTemplate.contentRequests.api.success.removedFromSiteTemplate"),
        });
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: err.message,
      });
    },
  });
}
